import { roundM } from '../Money';
import { isNullOrUndefined } from 'util';
import {
  OnlineOrder,
  BaseOrderingStore,
  OnlineOrderCustomFeeSettings,
  OnlineOrderDeliverySettings
} from 'kiolyn-api';
import { getApplyTax } from './KioskApplyTax';

export default function updateOrderCalculatedValues(
  order: OnlineOrder,
  store: BaseOrderingStore
): OnlineOrder {
  let subtotal = 0;
  let quantity = 0;
  let nontaxableTotal = 0;

  order.items.forEach(item => {
    let itemSubtotal = 0;
    const count = item.count ?? 0;
    quantity += count;
    item.modifiers.forEach(modifier => {
      modifier.options.forEach(option => {
        if (isNullOrUndefined(option)) {
          return;
        }

        let allOptionsPrice = option.price ?? 0;
        option.suboptions?.forEach(sub => {
          allOptionsPrice += sub.price ?? 0;
        });
        const optionSubtotal = roundM(allOptionsPrice * count);
        option.subtotal = optionSubtotal;
        itemSubtotal += optionSubtotal;
      });
    });

    const itemPrice = item.price ?? 0;
    itemSubtotal += roundM(itemPrice * count);
    itemSubtotal += roundM((item.price_note ?? 0) * count);
    item.subtotal = itemSubtotal;

    if (item.IsPromotion) {
      item.promotion_price = -itemPrice;
      const promotionTotal = roundM(-itemPrice * count);
      itemSubtotal += promotionTotal;
    } else {
      item.promotion_price = 0;
    }
    subtotal += itemSubtotal;

    if (item.non_taxable || item.gift_card || item.gift_certificate) {
      nontaxableTotal += itemSubtotal;
    }
  });

  order.quantity = quantity;
  if (order.order_type === 'delivery' && 'delivery_settings' in store) {
    order.delivery_fee_amount = order.delivery_fee_amount ?? 0;
    const ds = (store as any).delivery_settings as OnlineOrderDeliverySettings;
    if (ds) {
      const dd = order.delivery_distance ?? 0;
      if (ds.delivery_type === 'owner' || ds.delivery_type === undefined) {
        order.delivery_fee_amount = 0;
        const freeDeliveryAmount = ds.free_delivery_amount ?? 0;
        if (freeDeliveryAmount > 0 && subtotal >= freeDeliveryAmount) {
          order.delivery_fee_amount = 0;
        } else if (ds.use_fixed_delivery_fee) {
          order.delivery_fee_amount = ds.fixed_delivery_fee ?? 0;
        } else {
          let steps = ds.delivery_fees ?? [];
          let index = steps.findIndex(s => dd < (s.distance ?? 0));
          if (index < 0) {
            // If it could not be found, means that it is greater than the greatest
            // and thus the greatest value should be used
            index = steps.length - 1;
          } else if (index > 0) {
            // Take the previous step, in case it is out of range
            // then take the first step
            index = index - 1;
          }
          order.delivery_fee_amount = steps[index]?.fee ?? 0;
        }
      }
    }
  } else {
    order.delivery_fee_amount = 0;
  }

  let discount_amount = 0;
  if (!isNullOrUndefined(order.discount)) {
    discount_amount = roundM(subtotal * order.discount.percent);
  }
  let tax_amount = 0;
  if (getApplyTax(store)) {
    if (
      !isNullOrUndefined(order.tax) &&
      !isNullOrUndefined(order.tax.percent)
    ) {
      tax_amount = roundM(
        (subtotal +
          order.delivery_fee_amount -
          discount_amount -
          nontaxableTotal) *
          order.tax.percent
      );
    }
    order.tax_amount = tax_amount;
  }

  order.subtotal = subtotal;
  order.discount_amount = discount_amount;

  const customFee = (store as any)
    .custom_fee_settings as OnlineOrderCustomFeeSettings;
  let customFeeAmount = 0;
  if (
    !isNullOrUndefined(customFee) &&
    !isNullOrUndefined(customFee.amount) &&
    customFee.amount > 0 &&
    subtotal > 0
  ) {
    customFeeAmount = customFee.amount;
    order.custom_fee = customFee;
  } else {
    customFeeAmount = 0;
    order.custom_fee = undefined;
  }

  const tipAmount = order.tip ?? 0;
  order.total =
    subtotal +
    order.delivery_fee_amount -
    discount_amount +
    tax_amount +
    tipAmount +
    customFeeAmount;

  return order;
}
